export const termsContent = `# Terms of Service

**Practicio**
**Effective Date:** [Insert Date]
**Last Updated:** [Insert Date]

---

## 1. Introduction

Welcome to Practicio ("Platform", "we", "us", or "our"), a client approval and project control platform built for architects and interior designers. Practicio is operated by [Company Legal Name], a company incorporated under the laws of India.

By accessing or using Practicio at app.practicio.io (the "Service"), you ("User", "Studio", "you") agree to be bound by these Terms of Service ("Terms"). If you are using Practicio on behalf of a firm or studio, you represent that you have the authority to bind that entity to these Terms.

If you do not agree to these Terms, do not use the Service.

---

## 2. Definitions

**"Studio"** means the architecture or interior design practice that has registered an account on Practicio.

**"Client"** means the end-client of a Studio who accesses the Platform via a Client Approval Portal link to review and approve proposals, change orders, or other documents.

**"Project Data"** means all proposals, BOQs, change orders, budgets, approvals, audit logs, and associated records created or uploaded through the Service.

**"Approval Record"** means any formally signed-off decision captured through the Platform, including proposal approvals and change order approvals, which carry the status of a formal professional record.

---

## 3. Eligibility and Registration

3.1 You must be at least 18 years of age to use Practicio.

3.2 You must provide accurate, current, and complete registration information. You are responsible for maintaining the confidentiality of your account credentials.

3.3 You are responsible for all activities that occur under your account.

3.4 Practicio is a professional B2B platform. You agree to use it solely in connection with legitimate architecture or interior design practice management.

---

## 4. The Service

4.1 Practicio provides Studios with tools to manage client approvals, proposals, bills of quantities, change orders, payment milestones, and audit logs in a structured, professional environment.

4.2 We reserve the right to modify, suspend, or discontinue any part of the Service at any time with reasonable notice. For material changes, we will notify registered users at least 14 days in advance.

4.3 Practicio is not a legal services provider, financial advisor, or accountant. Records created on the Platform are professional project records, not substitutes for formal legal contracts unless your Studio presents them as such under its own professional engagement with its clients.

---

## 5. Immutable Records and Audit Integrity

5.1 Practicio's core architecture creates immutable, timestamped records of approvals, proposals, and change orders. Once a record is approved and locked, it cannot be altered through the Platform.

5.2 You acknowledge that these records are designed to serve as professional documentation and may carry evidentiary weight. You are responsible for ensuring the accuracy of all information you submit before requesting client approval.

5.3 You must not attempt to circumvent, manipulate, or interfere with the audit log, approval state, or versioning systems of the Platform.

---

## 6. Client Portal Access

6.1 When you share a Client Approval Portal link with your clients, you represent that:
- You have a legitimate professional relationship with that client.
- The documents being shared are accurate and reflect your genuine professional engagement.
- Your client has consented to receiving and interacting with documents through this Platform.

6.2 Practicio is not a party to any agreement between you and your clients. We provide the infrastructure; the professional relationship and any resulting obligations remain solely between you and your client.

---

## 7. Acceptable Use

You agree not to use the Platform to:

- Submit false, misleading, or fraudulent proposals or approvals.
- Share access with unauthorized third parties.
- Attempt to reverse-engineer, scrape, or extract Platform data through automated means.
- Transmit malware, spam, or disruptive code.
- Violate any applicable law, regulation, or professional code of conduct.

A full Acceptable Use Policy is published separately and incorporated herein by reference.

---

## 8. Intellectual Property

8.1 **Your Content.** You retain all ownership of Project Data you create on Practicio. By using the Service, you grant Practicio a limited, non-exclusive licence to store, process, and display your content solely for the purpose of providing the Service.

8.2 **Our Platform.** Practicio, its codebase, design, branding, and documentation are the intellectual property of [Company Legal Name]. You may not copy, reproduce, or create derivative works from the Platform without written permission.

---

## 9. Billing and Payment

9.1 Access to certain features is subject to a paid subscription plan. Billing terms, pricing, and plan features are described on the Platform and may be updated with notice.

9.2 All fees are non-refundable unless otherwise stated or required by applicable law.

9.3 Failure to pay may result in downgrade or suspension of your account.

9.4 Automated billing via Stripe or other payment processors will be available as indicated on the Platform. Until automated billing is live, payment terms will be communicated directly.

---

## 10. Data and Privacy

Your use of Practicio is also governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Service, you consent to our data practices as described therein.

---

## 11. Confidentiality

Both parties agree to keep confidential any non-public information received from the other in connection with the Service, and not to disclose it to third parties without prior written consent, except as required by law.

---

## 12. Disclaimer of Warranties

The Service is provided "as is" and "as available." To the maximum extent permitted by applicable law, Practicio disclaims all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement.

We do not warrant that the Service will be error-free, uninterrupted, or free of security vulnerabilities, though we take commercially reasonable steps to maintain availability and security.

---

## 13. Limitation of Liability

To the maximum extent permitted by law, Practicio's total liability to you for any claim arising out of or relating to these Terms or the Service shall not exceed the fees you paid to Practicio in the three (3) months preceding the claim.

In no event shall Practicio be liable for indirect, incidental, consequential, or punitive damages, including loss of profits, data, or business opportunities, even if advised of the possibility of such damages.

---

## 14. Indemnification

You agree to indemnify and hold harmless Practicio, its directors, employees, and agents from any claims, losses, or damages arising out of your use of the Service, your violation of these Terms, or your infringement of any third-party rights.

---

## 15. Termination

15.1 You may terminate your account at any time by contacting support.

15.2 We may suspend or terminate your account if you breach these Terms, engage in fraudulent activity, or pose a risk to the integrity of the Platform.

15.3 Upon termination, your access to the Platform will cease. We will retain your Project Data for 90 days post-termination, during which you may request an export. After this period, data may be permanently deleted.

---

## 16. Governing Law and Dispute Resolution

16.1 These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles.

16.2 Any dispute arising out of or relating to these Terms shall first be attempted to be resolved through good-faith negotiation. If unresolved within 30 days, disputes shall be subject to the exclusive jurisdiction of the courts of [City, India].

16.3 For users outside India, we will make reasonable efforts to resolve disputes efficiently and fairly.

---

## 17. Changes to These Terms

We may update these Terms from time to time. We will notify you of material changes via email or in-platform notice at least 14 days before they take effect. Continued use of the Service after the effective date constitutes acceptance of the revised Terms.

---

## 18. Contact

For questions about these Terms, contact us at:
**[Company Legal Name]**
Email: legal@practicio.io
Website: app.practicio.io
`;

export const privacyContent = `# Privacy Policy

**Practicio**
**Effective Date:** [Insert Date]
**Last Updated:** [Insert Date]

---

## 1. Overview

Practicio ("we", "us", "our") is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, store, and share information when you use our platform at app.practicio.io.

Practicio is operated by [Company Legal Name], incorporated in India. This policy is designed to comply with India's Information Technology Act, 2000 and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 ("SPDI Rules"), and to reflect best practices aligned with international frameworks including GDPR (EU), for the benefit of our international users.

---

## 2. Who This Policy Applies To

This policy applies to:

- **Studios** -- Architecture or interior design practices registered on Practicio.
- **Studio Users** -- Individual team members within a Studio who access the Platform.
- **Clients** -- End-clients of Studios who access the Client Approval Portal.
- **Visitors** -- Anyone who visits app.practicio.io without a registered account.

---

## 3. Information We Collect

### 3.1 Information You Provide

- **Account Registration:** Name, email address, company/practice name, phone number (optional), billing details.
- **Project Data:** Proposals, BOQs, change orders, budgets, payment milestones, and all associated records you create or upload.
- **Client Information:** Names and email addresses of your clients, as provided by you when sharing Client Portal links.
- **Communications:** Support requests, feedback, or correspondence you send us.

### 3.2 Information Collected Automatically

- **Usage Data:** Pages visited, features used, actions taken on the Platform, timestamps.
- **Device and Browser Data:** IP address, browser type, operating system, device identifiers.
- **Log Data:** API calls, error logs, session data.

### 3.3 Information from Third Parties

- **Email Delivery:** We use Resend to send transactional emails. Delivery status and open/bounce events may be recorded.
- **Payment Processors:** When billing is activated, payment data is handled by our payment provider (e.g., Stripe). We do not store full card details on our servers.

---

## 4. How We Use Your Information

We use your information to:

- Provide, operate, and maintain the Practicio platform.
- Generate and maintain immutable approval records, audit logs, and versioned project documents.
- Send transactional emails (e.g., proposal links, change order notifications, RFQ notifications).
- Process billing and manage your subscription.
- Respond to support requests and communicate platform updates.
- Detect and prevent fraud, abuse, or security incidents.
- Comply with our legal obligations.
- Improve the platform based on aggregate, anonymised usage data.

We do not use your Project Data to train machine learning models, sell to advertisers, or share with third parties for marketing purposes.

---

## 5. Audit Logs and Immutable Records

Practicio's core functionality creates permanent, timestamped records of business events including approvals, rejections, document exports, and email sends. These records are architectural features of the Platform -- they cannot be selectively deleted by Studios or Clients without platform-level intervention.

You should be aware that:

- Approval records created by your clients constitute formal recorded decisions.
- Audit log entries are retained for the duration of your account and for a defined period post-termination.
- If you request deletion of personal data, we will assess whether the data is part of a locked immutable record. Where legally required, we will anonymise rather than delete such records to preserve audit integrity.

---

## 6. Lawful Basis for Processing (GDPR -- International Users)

For users in the European Economic Area or United Kingdom, we process personal data under the following lawful bases:

- **Contract Performance:** To provide the Service you have signed up for.
- **Legitimate Interests:** To detect fraud, improve security, and maintain platform integrity.
- **Legal Obligation:** To comply with applicable laws.
- **Consent:** For optional communications. You may withdraw consent at any time.

---

## 7. Data Sharing

We do not sell your personal data. We share data only in the following circumstances:

- **Service Providers:** Infrastructure (MongoDB Atlas), email delivery (Resend), PDF generation, and payment processing. These providers process data on our behalf under appropriate data protection terms.
- **Client Portal:** When you share a Client Portal link, limited information (proposal content, your studio name) is made accessible to your designated client.
- **Legal Requirements:** If required by law, court order, or government authority.
- **Business Transfers:** In the event of a merger, acquisition, or sale of assets, data may be transferred. We will notify users if this occurs.

---

## 8. Data Retention

- **Active accounts:** Project Data is retained for the duration of your account.
- **Post-termination:** We retain data for 90 days after account closure to allow export. After this, data is deleted or anonymised.
- **Audit Logs:** Certain audit records may be retained for up to 7 years for legal and compliance purposes, in anonymised or pseudonymised form where possible.
- **Client data:** Client names and email addresses are retained as long as the associated Studio account is active or as required for legal record-keeping.

---

## 9. Data Security

We implement commercially reasonable technical and organisational measures to protect your data, including:

- Encrypted data in transit (TLS).
- Access controls and role-based permissions within the Platform.
- Audit logging of all significant actions.
- Managed infrastructure with security practices maintained by our hosting provider.

No method of transmission or storage is 100% secure. We will notify affected users of any data breach in accordance with applicable law.

---

## 10. International Data Transfers

Practicio's servers are currently hosted on managed infrastructure. If your data is processed or stored outside India or the EEA, we ensure appropriate safeguards are in place, such as standard contractual clauses where required.

---

## 11. Your Rights

Depending on your jurisdiction, you may have the right to:

- **Access** the personal data we hold about you.
- **Correct** inaccurate personal data.
- **Delete** your personal data (subject to our audit retention obligations above).
- **Restrict** or object to certain processing.
- **Data Portability** -- receive your data in a structured, machine-readable format.
- **Withdraw Consent** for any processing based on consent.

To exercise any of these rights, email us at privacy@practicio.io. We will respond within 30 days.

Note: Where data is part of an immutable audit record, we may not be able to fully delete it but will anonymise it wherever legally permissible.

---

## 12. Cookies and Tracking

Practicio uses essential cookies required for the Platform to function (e.g., session management). We do not currently use advertising or third-party tracking cookies. If this changes, we will update this policy and provide appropriate consent mechanisms.

---

## 13. Children's Privacy

The Platform is not intended for individuals under 18 years of age. We do not knowingly collect personal data from minors.

---

## 14. Changes to This Policy

We may update this Privacy Policy from time to time. We will notify you of material changes via email or in-platform notice. The "Last Updated" date at the top indicates when the policy was last revised.

---

## 15. Contact and Grievance Officer

For privacy-related queries or to exercise your rights, contact:

**Privacy Contact / Grievance Officer**
[Name of Grievance Officer]
[Company Legal Name]
Email: privacy@practicio.io
Address: [Registered Address]

Indian users may also file a complaint with the relevant authority under the IT Act, 2000.
`;

export const acceptableUseContent = `# Acceptable Use Policy

**Practicio**
**Effective Date:** [Insert Date]
**Last Updated:** [Insert Date]

---

## 1. Purpose

This Acceptable Use Policy ("AUP") sets out the rules governing how you may use the Practicio platform at app.practicio.io. It is incorporated by reference into the Practicio Terms of Service.

Practicio is a professional platform designed for architecture and interior design practices. The standards in this policy reflect the professional nature of the Platform and the trust that clients place in records generated through it.

---

## 2. Permitted Use

You may use Practicio to:

- Manage proposals, BOQs, change orders, and budgets for legitimate architecture or interior design projects.
- Share Client Approval Portal links with genuine clients of your practice.
- Maintain professional project records and audit trails.
- Collaborate with team members within your registered Studio.
- Export project documentation for professional use.

---

## 3. Prohibited Conduct

### 3.1 Integrity Violations

You must not:

- Submit false, fabricated, or misleading proposals, change orders, or BOQs through the Platform.
- Solicit or record approvals from individuals who are not your genuine clients, or who have not consented to the approval process.
- Attempt to manipulate, forge, or retroactively alter approval records, audit logs, or version histories.
- Use the Platform to create fraudulent documentation intended to deceive clients, regulators, lenders, or any other party.

### 3.2 Unauthorised Access

You must not:

- Share your account credentials with anyone outside your registered Studio.
- Access another Studio's account, data, or client portal without authorisation.
- Attempt to probe, scan, or test the Platform for vulnerabilities without prior written permission from Practicio.
- Use automated bots, scrapers, or scripts to extract data from the Platform.

### 3.3 System Abuse

You must not:

- Upload or transmit malware, viruses, ransomware, or any malicious code.
- Conduct denial-of-service attacks or otherwise attempt to disrupt the Platform's availability.
- Circumvent, disable, or interfere with Platform security features, access controls, or rate limiting.
- Exploit API endpoints for purposes outside the intended use of the Service.

### 3.4 Unlawful and Harmful Use

You must not use Practicio to:

- Violate any applicable law or regulation, including those related to data protection, professional conduct, or consumer protection.
- Harass, threaten, or harm any individual.
- Discriminate against clients or team members based on any protected characteristic.
- Engage in or facilitate money laundering, fraud, or any other financial crime.

### 3.5 Misrepresentation

You must not:

- Impersonate another studio, individual, or entity on the Platform.
- Represent the Platform's records as legally binding contracts in jurisdictions where this would require additional formality, without taking appropriate legal steps outside the Platform.
- Use the Practicio name, branding, or platform features in a way that implies endorsement of your services by Practicio.

---

## 4. Client Portal Responsibilities

When you share a Client Approval Portal link with a client, you represent and warrant that:

- The client is a real individual or entity in a genuine commercial relationship with your Studio.
- The client has been informed that they are accessing a professional project management platform and that their approvals will be recorded as formal decisions.
- You have a legitimate basis for sharing the client's email address and project information with the Platform.

---

## 5. Professional Standards

Practicio is used by design professionals whose work carries legal and financial consequences. You agree to use the Platform in a manner consistent with the professional standards applicable to architects and interior designers in your jurisdiction, including applicable codes of conduct issued by relevant professional bodies.

---

## 6. Reporting Violations

If you become aware of a violation of this policy -- including a suspected security incident, fraudulent use, or misuse of the Client Portal -- please report it immediately to: abuse@practicio.io

---

## 7. Consequences of Violation

Violations of this policy may result in:

- Immediate suspension or termination of your account.
- Retention of relevant records for legal purposes.
- Referral to law enforcement where required.
- Civil or legal action where applicable.

We reserve the right to determine, at our sole discretion, whether conduct constitutes a violation of this policy.

---

## 8. Changes to This Policy

We may update this AUP as the Platform evolves. We will notify registered users of material changes in accordance with the Terms of Service.

---

## 9. Contact

For questions about this policy:
Email: legal@practicio.io
`;

export const dpaContent = `# Data Processing Agreement

**Practicio**
**Effective Date:** [Insert Date]
**Last Updated:** [Insert Date]

---

## Preamble

This Data Processing Agreement ("DPA") is entered into between [Company Legal Name] ("Practicio", "Processor") and the Studio registering an account on the Platform ("Studio", "Controller") and forms part of the Practicio Terms of Service.

This DPA applies where Practicio processes personal data on behalf of the Studio in connection with providing the Service -- in particular, personal data relating to the Studio's clients, team members, and project counterparties.

This DPA is intended to satisfy obligations under applicable data protection law, including India's Information Technology Act 2000 and SPDI Rules 2011, and for Studios with users in the EU/EEA/UK, to reflect the requirements of the General Data Protection Regulation (EU) 2016/679 ("GDPR") and UK GDPR.

---

## 1. Definitions

**"Controller"** means the Studio, which determines the purposes and means of processing personal data.

**"Processor"** means Practicio, which processes personal data on behalf of the Controller.

**"Personal Data"** means any information relating to an identified or identifiable natural person, as defined under applicable data protection law.

**"Processing"** means any operation performed on personal data, including storage, retrieval, transmission, and deletion.

**"Data Subject"** means any identified or identifiable individual whose personal data is processed under this DPA -- including Studio clients, team members, and project counterparties.

**"Sub-processor"** means any third-party processor engaged by Practicio to process personal data on behalf of the Controller.

---

## 2. Roles and Scope

2.1 For personal data that Studios upload, enter, or generate on the Platform (including client names, email addresses, and project information), the Studio is the Controller and Practicio is the Processor.

2.2 For account registration data and platform usage data collected directly from Studio Users, Practicio acts as an independent Controller, as described in the Privacy Policy.

2.3 Practicio will process personal data only on documented instructions from the Studio (including as set out in this DPA and the Terms of Service) and not for Practicio's own purposes, except where required by applicable law.

---

## 3. Nature and Purpose of Processing

**Purpose:** Providing the Practicio project management and client approval platform, including proposal management, change order workflows, audit logging, PDF generation, and email notifications.

**Nature:** Storage, retrieval, structured organisation, transmission (via email/portal links), and export of project and client records.

**Types of Personal Data:**
- Client names and email addresses
- Studio team member names and contact details
- Approval decisions and timestamps attributed to named individuals
- Communication content within the platform

**Categories of Data Subjects:**
- Studio clients
- Studio team members and directors
- Project counterparties referenced in project documents

**Duration:** For the duration of the Studio's active account, and as described in the Retention section below.

---

## 4. Practicio's Obligations as Processor

Practicio shall:

4.1 Process personal data only for the purposes described in this DPA and in accordance with the Studio's documented instructions, except where required otherwise by applicable law (in which case Practicio will inform the Studio, unless prohibited).

4.2 Ensure that personnel authorised to process personal data are bound by appropriate confidentiality obligations.

4.3 Implement appropriate technical and organisational security measures, including encrypted data in transit, role-based access controls, and audit logging of system access.

4.4 Assist the Studio in responding to Data Subject rights requests (access, correction, deletion, portability) within a reasonable timeframe, taking into account the nature of the processing. Note: Where data is part of an immutable audit record, deletion may be replaced with anonymisation where permitted by law.

4.5 Notify the Studio without undue delay (and in any event within 72 hours where required) upon becoming aware of a personal data breach affecting Studio data.

4.6 Assist the Studio in meeting its compliance obligations regarding security, breach notification, and data protection impact assessments, to the extent reasonably practicable given the nature of the processing.

4.7 At the Studio's request and upon account termination, delete or return all personal data in Practicio's possession (except as required to be retained by law or for audit integrity purposes, in which case anonymisation will be applied where possible).

4.8 Make available to the Studio all information reasonably necessary to demonstrate compliance with this DPA, and cooperate with audits or inspections, provided reasonable notice is given and such audits do not unduly disrupt Practicio's operations.

---

## 5. Studio's Obligations as Controller

The Studio shall:

5.1 Ensure there is a lawful basis for sharing personal data with Practicio and for the processing described in this DPA.

5.2 Ensure that Data Subjects (particularly clients) have been appropriately informed about the use of Practicio to manage project records, approvals, and communications.

5.3 Ensure that any personal data provided to Practicio is accurate, relevant, and not excessive for the purpose.

5.4 Not instruct Practicio to process personal data in a manner that would violate applicable law.

---

## 6. Sub-processors

6.1 The Studio provides general written authorisation for Practicio to engage the following categories of sub-processors:

| Sub-processor | Purpose | Location |
|---|---|---|
| MongoDB Atlas | Database infrastructure | Managed |
| Resend | Transactional email delivery | USA |
| Stripe | Payment processing | USA/Global |
| ReportLab | PDF generation and serving | Managed |

6.2 Practicio will inform the Studio of any intended changes to sub-processors (additions or replacements) with at least 14 days' notice, giving the Studio the opportunity to object. If the Studio objects and Practicio cannot accommodate the objection, the Studio may terminate the Service.

6.3 Practicio ensures that sub-processors are bound by data protection obligations equivalent to those in this DPA.

---

## 7. International Data Transfers

Where personal data is transferred outside India or the European Economic Area, Practicio will ensure that such transfers are made subject to appropriate safeguards, including:

- Standard Contractual Clauses (SCCs) as approved by the European Commission, where applicable.
- Equivalent contractual protections aligned with Indian data protection law.

---

## 8. Immutable Records and Audit Logs

8.1 The Studio acknowledges that certain platform features -- specifically approval records, version histories, and audit logs -- are immutable by design. These cannot be selectively deleted once created.

8.2 Where a Data Subject requests deletion of personal data that forms part of an immutable record, Practicio will work with the Studio to anonymise the record (replacing identifying data with pseudonyms or redacted identifiers) rather than deleting the record, in order to preserve audit integrity while protecting the individual's privacy rights.

8.3 The Studio is responsible for informing its clients of this characteristic of the Platform where relevant.

---

## 9. Liability

9.1 Each party shall be liable for damage caused by processing that infringes applicable data protection law to the extent it is responsible for that infringement.

9.2 Practicio's liability under this DPA is limited as set out in the Terms of Service.

---

## 10. Term and Termination

This DPA remains in effect for the duration of the Terms of Service. On termination of the Terms of Service, this DPA terminates automatically. Data handling obligations on termination are as described in Section 4.7 above and the Privacy Policy.

---

## 11. Governing Law

This DPA shall be governed by the laws of India. For Studios in the EU/UK, any conflict between this DPA and applicable EU/UK data protection law shall be resolved in favour of the applicable law.

---

## 12. Contact

For data protection matters:
**[Company Legal Name]**
Email: privacy@practicio.io
Address: [Registered Address]
`;
